// RMS Page - Professional Design with 3D Animations
// Modern UI/UX with consistent color scheme

import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import rmsSolutionImage from './images/rms solution.png';
import {
  Cpu, Database, MessageSquare, Layers, Globe, Shield, Phone, CreditCard,
  BarChart3, DollarSign, Share2, Award, Zap, CheckCircle, 
  MessageCircle, Headphones, Network, FileCheck, Unplug, Lock,
  TrendingUp, ArrowRight, Check, Users, Bot, FileText, RefreshCw,
  Target, Calculator, Settings, Workflow, PieChart, LineChart,
  Calendar, Clock, AlertTriangle, TrendingDown
} from 'lucide-react';

// Enhanced 3D Animated NetworkLattice
const NetworkLattice = () => {
  return (
    <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <pattern id="rms-grid" width="40" height="40" patternUnits="userSpaceOnUse">
          <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(227,30,36,0.1)" strokeWidth="0.5"/>
        </pattern>
        <radialGradient id="grid-fade">
          <stop offset="0%" stopColor="rgba(227,30,36,0.15)" />
          <stop offset="100%" stopColor="rgba(227,30,36,0)" />
        </radialGradient>
        <filter id="glow">
          <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      <rect width="100%" height="100%" fill="url(#rms-grid)" />
      <circle cx="50%" cy="50%" r="40%" fill="url(#grid-fade)" filter="url(#glow)" />
    </svg>
  );
};

// Animated 3D Particles Background
const ParticlesBackground = () => {
  const particles = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: Math.random() * 4 + 2,
    duration: Math.random() * 20 + 10,
    delay: Math.random() * 5
  }));

  return (
    <div className="absolute inset-0 overflow-hidden">
      {particles.map((particle) => (
        <motion.div
          key={particle.id}
          className="absolute rounded-full bg-[#ff5d22]/20"
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            width: particle.size,
            height: particle.size,
          }}
          animate={{
            y: [0, -30, 0],
            x: [0, Math.random() * 20 - 10, 0],
            opacity: [0.2, 0.5, 0.2],
            scale: [1, 1.5, 1],
          }}
          transition={{
            duration: particle.duration,
            repeat: Infinity,
            delay: particle.delay,
            ease: "easeInOut"
          }}
        />
      ))}
    </div>
  );
};

// Floating Gradient Orbs
const FloatingOrbs = ({ count = 3 }: { count?: number }) => {
  const orbs = Array.from({ length: count }, (_, i) => ({
    id: i,
    size: Math.random() * 400 + 300,
    x: Math.random() * 100,
    y: Math.random() * 100,
    color: i % 2 === 0 ? 'bg-[#ff5d22]/10' : 'bg-slate-700/20',
    duration: Math.random() * 20 + 15
  }));

  return (
    <>
      {orbs.map((orb) => (
        <motion.div
          key={orb.id}
          className={`absolute ${orb.color} rounded-full blur-[120px]`}
          style={{
            width: orb.size,
            height: orb.size,
            left: `${orb.x}%`,
            top: `${orb.y}%`,
          }}
          animate={{
            x: [0, Math.random() * 100 - 50, 0],
            y: [0, Math.random() * 100 - 50, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: orb.duration,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      ))}
    </>
  );
};

// 3D Floating Card Component with simplified hover
const FloatingCard: React.FC<{ children: React.ReactNode; delay?: number; index?: number }> = ({ 
  children, 
  delay = 0,
  index = 0 
}) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-50px" }}
    transition={{ 
      delay, 
      duration: 0.6,
      ease: "easeOut"
    }}
    whileHover={{ 
      y: -8,
      transition: { duration: 0.3 }
    }}
  >
    {children}
  </motion.div>
);

// Staggered Grid Layout Component for odd/even card handling
const SmartGrid: React.FC<{ 
  children: React.ReactNode[]; 
  columns?: number;
}> = ({ children, columns = 3 }) => {
  const totalCards = React.Children.count(children);
  const childrenArray = React.Children.toArray(children);
  
  // Calculate layout: if odd number, show last 2 cards full width
  const shouldUseSpecialLayout = totalCards % columns !== 0 && totalCards > columns;
  const regularCards = shouldUseSpecialLayout ? totalCards - 2 : totalCards;
  const specialCards = shouldUseSpecialLayout ? childrenArray.slice(regularCards) : [];
  const normalCards = shouldUseSpecialLayout ? childrenArray.slice(0, regularCards) : childrenArray;

  return (
    <div className="space-y-6">
      {/* Regular grid */}
      <div className={`grid gap-6 ${
        columns === 3 ? 'md:grid-cols-2 lg:grid-cols-3' :
        columns === 4 ? 'md:grid-cols-2 lg:grid-cols-4' :
        columns === 2 ? 'md:grid-cols-2' :
        'md:grid-cols-2 lg:grid-cols-3'
      }`}>
        {normalCards}
      </div>
      
      {/* Special layout for last 2 cards when odd */}
      {specialCards.length > 0 && (
        <div className="grid md:grid-cols-2 gap-6">
          {specialCards}
        </div>
      )}
    </div>
  );
};

// Animated Section Wrapper
const AnimatedSection: React.FC<{ 
  children: React.ReactNode; 
  className?: string;
  delay?: number;
}> = ({ children, className = "", delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0 }}
    whileInView={{ opacity: 1 }}
    viewport={{ once: true, margin: "-50px" }}
    transition={{ duration: 0.6, delay }}
    className={className}
  >
    {children}
  </motion.div>
);

interface RMSPageProps {
  data: any;
  theme: any;
  ProductHero: React.ComponentType<any>;
  ContactSection: React.ComponentType<any>;
  SectionHeader: React.ComponentType<any>;
  MockDashboard: React.ComponentType<any>;
  Button: React.ComponentType<any>;
}

export const RMSPageContent = ({
  data, theme, ProductHero, ContactSection, SectionHeader, MockDashboard, Button
}: RMSPageProps) => {
  const { scrollY } = useScroll();
  const heroParallax = useTransform(scrollY, [0, 500], [0, -100]);
  const sectionParallax = useTransform(scrollY, [0, 1000], [0, -50]);
  
  const [activeProblem, setActiveProblem] = useState(-1);
  const [activeFeature, setActiveFeature] = useState(-1);

  // RMS Industry Challenges
  const challenges = [
    { 
      icon: Calendar, 
      title: "Renewal Visibility Gaps", 
      desc: "Lack of centralized renewal tracking leads to missed opportunities and revenue leakage.",
      gradient: "from-[#ff5d22]/20 to-[#ff5d22]/5"
    },
    { 
      icon: TrendingDown, 
      title: "Churn Prediction Blind Spots", 
      desc: "Inability to predict and prevent customer churn results in unexpected revenue loss.",
      gradient: "from-slate-900/20 to-slate-900/5"
    },
    { 
      icon: Clock, 
      title: "Manual Renewal Processes", 
      desc: "Time-consuming manual workflows delay renewals and increase operational costs.",
      gradient: "from-[#ff5d22]/20 to-[#ff5d22]/5"
    },
    { 
      icon: AlertTriangle, 
      title: "Customer Health Monitoring", 
      desc: "Lack of real-time customer health insights prevents proactive intervention strategies.",
      gradient: "from-slate-900/20 to-slate-900/5"
    },
    { 
      icon: BarChart3, 
      title: "Revenue Forecasting", 
      desc: "Inaccurate renewal forecasting makes it difficult to predict and plan for future revenue.",
      gradient: "from-[#ff5d22]/20 to-[#ff5d22]/5"
    }
  ];

  // Core RMS Problems
  const problems = [
    { icon: Calendar, title: "Missed Renewals", desc: "Poor renewal tracking leads to missed opportunities and unexpected churn." },
    { icon: TrendingDown, title: "Churn Surprises", desc: "Lack of early warning systems results in unexpected customer losses." },
    { icon: Clock, title: "Manual Workflows", desc: "Time-consuming manual processes reduce team efficiency and increase errors." },
    { icon: BarChart3, title: "Poor Forecasting", desc: "Inaccurate revenue predictions make strategic planning difficult." }
  ];

  // RMS Solution Features
  const features = [
    { icon: BarChart3, title: "Renewal Intelligence Hub", desc: "Complete visibility into your renewal pipeline with health monitoring and risk assessment." },
    { icon: Bot, title: "AI-Powered Churn Prediction", desc: "Machine learning algorithms identify at-risk customers and recommend intervention strategies." },
    { icon: RefreshCw, title: "Automated Renewal Workflows", desc: "Streamlined processes for renewal notifications, approvals, and contract generation." },
    { icon: TrendingUp, title: "Revenue Forecasting", desc: "Predictive analytics for accurate renewal forecasting and revenue planning." },
    { icon: Users, title: "Customer Success Integration", desc: "Seamless integration with customer success tools for proactive account management." }
  ];

  // RMS Capabilities
  const capabilities = [
    { icon: Calendar, title: "Renewal Tracking", desc: "Centralized dashboard for all renewal activities and timelines" },
    { icon: Bot, title: "Churn Prediction", desc: "AI-powered algorithms to identify at-risk customers early" },
    { icon: RefreshCw, title: "Workflow Automation", desc: "Automated renewal processes and customer communications" },
    { icon: BarChart3, title: "Revenue Analytics", desc: "Advanced reporting and forecasting capabilities" },
    { icon: Users, title: "Customer Health", desc: "Real-time customer health scoring and monitoring" },
    { icon: Network, title: "CRM Integration", desc: "Seamless integration with existing CRM and CS platforms" }
  ];

  // Integration Ecosystem
  const integrations = [
    { category: "CRM Systems", items: ["Salesforce", "HubSpot", "Pipedrive"], icon: Users },
    { category: "Customer Success", items: ["Gainsight", "ChurnZero", "Totango"], icon: TrendingUp },
    { category: "Billing Systems", items: ["Stripe", "Zuora", "Chargebee"], icon: CreditCard },
    { category: "Communication", items: ["Slack", "Microsoft Teams", "Email"], icon: MessageCircle },
    { category: "Analytics", items: ["Tableau", "Power BI", "Looker"], icon: BarChart3 }
  ];

  // Key Differentiators
  const differentiators = [
    { icon: Bot, title: "Predictive Intelligence", desc: "Advanced AI algorithms predict churn risk and identify expansion opportunities." },
    { icon: RefreshCw, title: "Automation-First", desc: "Comprehensive workflow automation reduces manual effort by 85%." },
    { icon: BarChart3, title: "Revenue Optimization", desc: "Data-driven insights optimize renewal rates and customer lifetime value." },
    { icon: Network, title: "Unified Platform", desc: "Single platform integrating renewals, customer success, and revenue operations." }
  ];

  // Success Metrics
  const metrics = [
    { icon: TrendingUp, title: "Renewal Rate", desc: "Increase renewal rates through proactive customer management.", value: "+35%", badge: "Growth" },
    { icon: Clock, title: "Process Efficiency", desc: "Reduce manual renewal processing time through automation.", value: "85% Faster", badge: "Speed" },
    { icon: BarChart3, title: "Forecast Accuracy", desc: "Improve revenue forecasting accuracy with predictive analytics.", value: "+40%", badge: "Accuracy" },
    { icon: DollarSign, title: "Revenue Impact", desc: "Increase customer lifetime value through better retention.", value: "+25%", badge: "Revenue" }
  ];

  // Target Industries
  const targetIndustries = [
    {
      industry: "SaaS & Technology",
      companies: ["Salesforce", "HubSpot", "Zoom", "Slack", "Atlassian", "ServiceNow", "Workday"]
    },
    {
      industry: "Subscription Services",
      companies: ["Netflix", "Spotify", "Adobe", "Microsoft", "Amazon", "Box", "Dropbox"]
    },
    {
      industry: "Professional Services",
      companies: ["Accenture", "Deloitte", "PwC", "EY", "KPMG", "McKinsey", "BCG"]
    }
  ];

  return (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      exit={{ opacity: 0 }} 
      className="pt-20 bg-white overflow-hidden"
    >

      {/* Hero Section with Parallax */}
      <motion.div 
        style={{ y: heroParallax }} 
        className="max-w-7xl mx-auto px-6 md:px-12 mb-12 relative"
      >
        <div className="absolute -inset-20 opacity-20">
          <NetworkLattice />
        </div>
        <div className="relative z-10">
          <ProductHero data={data} theme={theme} />
        </div>
      </motion.div>

      {/* Industry Challenges - Compact Dark Section */}
      <section className="py-10 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white relative overflow-hidden">
        {/* Animated Background */}
        <motion.div 
          style={{ y: sectionParallax }}
          className="absolute inset-0 opacity-10"
        >
          <NetworkLattice />
        </motion.div>
        
        {/* Floating Orbs */}
        <FloatingOrbs count={2} />
    
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <AnimatedSection delay={0.1}>
            <div className="text-center max-w-3xl mx-auto mb-8">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="inline-block px-4 py-1.5 rounded-full bg-[#ff5d22]/20 border border-[#ff5d22]/30 mb-3"
              >
                <span className="text-xs font-black uppercase tracking-widest text-[#ff5d22]">Industry Challenges</span>
              </motion.div>
              <h2 className="text-2xl md:text-3xl tracking-tight mb-2 text-white/90 leading-tight">
                Breaking the Barriers in <span className="text-[#ff5d22]">Renewal Management</span>
              </h2>
              <p className="text-white/60 text-xs font-medium">
                The critical challenges holding back revenue teams from achieving predictable recurring growth.
              </p>
            </div>
          </AnimatedSection>
          
          {/* Smart Grid: 3 cards in first row, 2 cards full-width in second row */}
          <SmartGrid columns={3}>
            {challenges.map((challenge, i) => (
              <FloatingCard key={i} delay={i * 0.08} index={i}>
                <div className={`h-full p-4 rounded-xl bg-gradient-to-br ${challenge.gradient} backdrop-blur-xl border border-white/10 hover:border-white/30 transition-all duration-300 group`}>
                  <div className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center mb-3 group-hover:bg-white/10 transition-all duration-300">
                    <challenge.icon size={20} className="text-white/80" strokeWidth={1.5} />
                  </div>
                  <h4 className="text-sm font-black text-white mb-2 leading-tight">{challenge.title}</h4>
                  <p className="text-white/60 text-xs leading-relaxed font-medium">{challenge.desc}</p>
                </div>
              </FloatingCard>
            ))}
          </SmartGrid>
        </div>
      </section>

      {/* The Challenge Section - Compact */}
      <section className="py-12 bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-slate-50" />
        
        <motion.div 
          style={{ y: sectionParallax, opacity: 0.3 }}
          className="absolute inset-0"
        >
          <NetworkLattice />
        </motion.div>
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <AnimatedSection>
            <div className="text-center max-w-3xl mx-auto mb-10">
              <div className="inline-block px-4 py-1.5 rounded-full bg-slate-100 border border-slate-200 mb-3">
                <span className="text-xs font-black uppercase tracking-widest text-slate-600">The Challenge</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-black tracking-tight mb-3 leading-tight text-slate-900">
                Revenue Teams <span className="text-[#ff5d22]">Struggle With</span>
              </h2>
              <p className="text-slate-600 text-sm font-medium">
                These inefficiencies directly impact renewal rates, customer retention, and revenue predictability.
              </p>
            </div>
          </AnimatedSection>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {problems.map((problem, i) => (
              <FloatingCard key={i} delay={i * 0.08} index={i}>
                <div
                  onMouseEnter={() => setActiveProblem(i)}
                  onMouseLeave={() => setActiveProblem(-1)}
                  className={`h-full p-5 rounded-xl border-2 transition-all duration-300 ${
                    activeProblem === i 
                      ? 'bg-[#ff5d22] border-[#ff5d22] shadow-xl shadow-[#ff5d22]/20' 
                      : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-lg'
                  }`}
                >
                  <div className={`w-11 h-11 rounded-lg flex items-center justify-center mb-3 transition-all duration-300 ${
                    activeProblem === i ? 'bg-white/20' : 'bg-slate-100'
                  }`}>
                    <problem.icon 
                      size={22} 
                      className={activeProblem === i ? 'text-white' : 'text-[#ff5d22]'} 
                      strokeWidth={1.5}
                    />
                  </div>
                  <h4 className={`text-sm font-black mb-2 leading-tight transition-colors ${
                    activeProblem === i ? 'text-white' : 'text-slate-900'
                  }`}>
                    {problem.title}
                  </h4>
                  <p className={`text-xs leading-relaxed font-medium transition-colors ${
                    activeProblem === i ? 'text-white/80' : 'text-slate-600'
                  }`}>
                    {problem.desc}
                  </p>
                </div>
              </FloatingCard>
            ))}
          </div>
        </div>
      </section>

      {/* Our Solution Section - Split Layout */}
      <section className="py-20 bg-slate-50 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <NetworkLattice />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection delay={0.2}>
              <div className="space-y-8">
                <div>
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    className="inline-block px-4 py-2 rounded-full bg-white border border-slate-200 mb-6"
                  >
                    <span className="text-xs font-black uppercase tracking-widest text-slate-600">Our Solution</span>
                  </motion.div>
                  <h2 className="text-4xl md:text-3xl font-black tracking-tight mb-6 leading-tight text-slate-900">
                    Your Renewal Command Center <span className="text-[#ff5d22]"><br></br>Predictable Revenue Growth</span>
                  </h2>
                  <p className="text-slate-600 text-lg font-medium leading-relaxed">
                    Automate renewals, predict churn, and unlock predictable recurring growth with intelligent revenue orchestration.
                  </p>
                </div>
                
                <div className="space-y-4">
                  {[
                    "AI-powered churn & revenue prediction",
                    "Automated renewal workflows",
                    "Renewal bulk data ingestion",
                    "Renewal notification/remainders",
                    "Sequential and parallel approval workflows"
                  ].map((feature, i) => (
                    <motion.div 
                      key={i}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.1 }}
                      className="flex items-center gap-4 group"
                    >
                      <div className="w-10 h-10 rounded-xl bg-[#ff5d22]/10 flex items-center justify-center group-hover:bg-[#ff5d22] transition-all duration-300">
                        <CheckCircle size={20} className="text-[#ff5d22] group-hover:text-white transition-colors" strokeWidth={2} />
                      </div>
                      <span className="text-slate-700 font-bold text-base group-hover:text-slate-900 transition-colors">{feature}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.4}>
              <div className="relative">
                <div className="absolute -inset-8 bg-gradient-to-br from-[#ff5d22]/20 to-transparent blur-3xl rounded-[3rem]" />
                <div className="relative bg-white rounded-[2.5rem] border border-slate-200 p-6 shadow-2xl overflow-hidden">
                  <motion.img
                    src={rmsSolutionImage}
                    alt="RMS solution"
                    referrerPolicy="no-referrer"
                    className="w-full h-full object-fill rounded-[2rem] transition-transform duration-500 ease-out hover:scale-[1.02]"
                  />
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Platform Features - Compact Grid */}
      <section className="py-12 bg-white relative overflow-hidden">
        <motion.div 
          style={{ y: sectionParallax, opacity: 0.2 }}
          className="absolute inset-0"
        >
          <NetworkLattice />
        </motion.div>
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <AnimatedSection>
            <div className="text-center max-w-3xl mx-auto mb-9">
              <div className="inline-block px-4 py-2 rounded-full bg-slate-100 border border-slate-200 mb-4">
                <span className="text-xs font-black uppercase tracking-widest text-slate-600">Platform Features</span>
              </div>
              <h2 className="text-2xl md:text-3xl font-black tracking-tight mb-3 leading-tight text-slate-900">
                Comprehensive <span className="text-[#ff5d22]">RMS Capabilities</span>
              </h2>
              <p className="text-slate-600 text-sm font-medium">
                A complete platform covering every aspect of renewal management and revenue optimization.
              </p>
            </div>
          </AnimatedSection>
          
          {/* Platform Features grid: use 2x2 "big-small / small-big" for exactly 4 items, otherwise fall back to SmartGrid */}
          {features.length === 4 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {features.map((feature, i) => (
                <FloatingCard key={i} delay={i * 0.08} index={i}>
                  <div
                    onMouseEnter={() => setActiveFeature(i)}
                    onMouseLeave={() => setActiveFeature(-1)}
                    className={`h-full p-5 rounded-2xl bg-gradient-to-br from-slate-50 to-white border border-slate-200 hover:border-slate-300 hover:shadow-lg transition-all duration-300 group ${
                      (i === 0 || i === 3) ? 'min-h-[240px]' : 'min-h-[150px]'
                    }`}
                  >
                    <div className="w-12 h-12 rounded-xl bg-white border border-slate-200 flex items-center justify-center mb-3 group-hover:bg-slate-900 group-hover:border-slate-900 transition-all duration-300 shadow-sm">
                      <feature.icon size={24} className="text-slate-700 group-hover:text-white transition-colors" strokeWidth={1.5} />
                    </div>
                    <h4 className="text-base font-black text-slate-900 mb-2 leading-tight">{feature.title}</h4>
                    <p className="text-slate-600 text-sm leading-relaxed font-medium">{feature.desc}</p>
                  </div>
                </FloatingCard>
              ))}
            </div>
          ) : (
            <SmartGrid columns={3}>
              {features.map((feature, i) => (
                <FloatingCard key={i} delay={i * 0.08} index={i}>
                  <div 
                    onMouseEnter={() => setActiveFeature(i)}
                    onMouseLeave={() => setActiveFeature(-1)}
                    className="h-full p-5 rounded-2xl bg-gradient-to-br from-slate-50 to-white border border-slate-200 hover:border-slate-300 hover:shadow-lg transition-all duration-300 group"
                  >
                    <div className="w-12 h-12 rounded-xl bg-white border border-slate-200 flex items-center justify-center mb-3 group-hover:bg-slate-900 group-hover:border-slate-900 transition-all duration-300 shadow-sm">
                      <feature.icon size={24} className="text-slate-700 group-hover:text-white transition-colors" strokeWidth={1.5} />
                    </div>
                    <h4 className="text-base font-black text-slate-900 mb-2 leading-tight">{feature.title}</h4>
                    <p className="text-slate-600 text-sm leading-relaxed font-medium">{feature.desc}</p>
                  </div>
                </FloatingCard>
              ))}
            </SmartGrid>
          )}
        </div>
      </section>

      {/* Contact Section */}
      <ContactSection id="contact" />
    </motion.div>
  );
};